import React, { useState, useEffect, useCallback, useMemo } from "react";
import { BiSolidChevronDown } from "react-icons/bi";
import { Popover, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../store/Slices/ProductSlice";

const ItemSelector = ({
  SelectedProduct,
  setSelectedProduct,
  SelectedCompany,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [SearchText, setSearchText] = useState("");

  const dispatch = useDispatch();
  const Products = useSelector((state) => state.ItemsState);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleProductSelect = useCallback(
    (product) => {
      setSelectedProduct(product);
      handleClose();
    },
    [setSelectedProduct, handleClose]
  );

  const open = Boolean(anchorEl);
  const id = open ? "item-selector-popover" : undefined;

  // Memoized filtered products for better performance
  const filteredProducts = useMemo(() => {
    if (!Products.data || !Array.isArray(Products.data)) return [];

    return Products.data.filter((product) => {
      // Filter by search text
      const matchesSearch =
        SearchText === "" ||
        product.name?.toLowerCase().includes(SearchText.toLowerCase());

      // Filter by selected company
      const matchesCompany =
        !SelectedCompany ||
        !SelectedCompany._id ||
        !product.companyId ||
        !product.companyId._id ||
        SelectedCompany._id === product.companyId._id;

      return matchesSearch && matchesCompany;
    });
  }, [Products.data, SearchText, SelectedCompany]);

  return (
    <div className="flex flex-col px-0 gap-y-4">
      <div
        // className="px-4 py-[6px] border-2 border-white rounded-full cursor-pointer bg-[#155f60] text-white"
        className={`relative text-left cursor-pointer transition-colors ease-in-out duration-200 border border-line hover:border-main bg-card text-ink px-4 py-3 rounded-lg font-quicksand font-bold text-sm flex gap-x-6 items-center active:scale-[.99]`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between w-full">
          <div className="w-full outline-none text-left bg-transparent">
            {SelectedProduct === "" ? "Select Item" : SelectedProduct?.name}
          </div>
          <BiSolidChevronDown
            className="text-[1.5rem] cursor-pointer text-muted"
            aria-describedby={id}
            variant="contained"
          />
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #E5E9EB",
            width: "100%", // Set the width as needed
            width: anchorEl ? anchorEl.offsetWidth : "auto", // 👈 match parent width
            overflow: "hidden", // Hide overflowing content
            marginTop: "10px",
            boxShadow: "0 10px 30px rgba(16,69,70,0.12), 0 2px 8px rgba(16,69,70,0.08)",
            overflowY: "auto",
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Typography
          sx={{
            p: 2,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            borderRadius: "12px",
          }}
        >
          <div className="bg-card text-ink font-quicksand flex flex-col justify-center items-center rounded-lg">
            <div className="w-full flex flex-col justify-between gap-y-3 items-start">
              <input
                type="text"
                placeholder="Search"
                name=""
                className="w-full px-3 py-1 rounded-xl text-ink"
                id=""
                value={SearchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {filteredProducts.map((product, index) => (
                <div
                  key={product._id || index}
                  className="flex gap-x-3 items-center cursor-pointer hover:bg-subtle rounded-lg px-2 py-1 transition-colors"
                  onClick={() => handleProductSelect(product)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleProductSelect(product);
                    }
                  }}
                  aria-label={`Select ${product.name}`}
                >
                  <input
                    type="checkbox"
                    className="mr-1 h-4 w-4 rounded border border-line accent-main"
                    checked={SelectedProduct?._id === product._id}
                    readOnly
                    tabIndex={-1}
                    onChange={() => {}} // Empty onChange to prevent warning
                  />
                  <span className="text-sm">{product.name}</span>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="text-center text-muted py-4">
                  {SearchText
                    ? "No products found matching your search"
                    : "No products available"}
                </div>
              )}
            </div>
          </div>
        </Typography>
      </Popover>
    </div>
  );
};

export default ItemSelector;
